/**
 * Redux slice for professionals feature.
 *
 * Epic: #917 Lawyer Profiles & Client-Lawyer Matching System
 *
 * Manages state for:
 * - Professional search results (standalone page)
 * - Two-phase search: instant name results + async topic enrichment
 * - Current profile view
 * - Inline match results in research chat
 */

import { CacheManager } from '@/lib/cache';
import ProfessionalsService from '@/services/professionals-services';
import type {
  BulkMatchRecipient,
  BulkMatchRecipientResult,
  MapPoint,
  MatchCreateResponse,
  MatchedProfessional,
  ProfessionType,
  ProfessionalRead,
  ProfessionalStatus,
  SearchFilters,
  TopicScoreEntry,
} from '@/types/professionals-types';
import {
  type PayloadAction,
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';
import { type AxiosError, isAxiosError } from 'axios';
import type { RootState } from '../store';

// ============================================================================
// STATE TYPES
// ============================================================================

interface MatchResultsState {
  professionals: MatchedProfessional[];
  /** Labelled paid placements (Profil+ · Inzercia) rendered above organic
   *  matches — top-3 candidates scoped to location + specialization (issue
   *  #4736, expands #3663 Phase 2). Empty when none qualify. */
  featuredSlots: MatchedProfessional[];
  /** Audit-log impression ids for ``featuredSlots`` (issue #3686),
   *  **index-aligned** with that array. The ``<FeaturedSlot/>`` component
   *  echoes the clicked slot's id back via
   *  ``ProfessionalsService.recordFeaturedClick`` so impressions and clicks
   *  join for CTR analytics. Empty when there are no slots OR when running
   *  against a backend that predates the audit-log instrumentation (graceful
   *  degradation — clicks just don't get tracked). */
  featuredSlotImpressionIds: string[];
  loading: boolean;
  error: string | null;
  isExpanded: boolean;
  dismissed: boolean;
}

/**
 * Per-professional contact-status surfacing (#3557 Phase 3.1-3.4).
 *
 * Keyed by ``professional_id`` so the chat-carousel card can look up
 * "have I already contacted this lawyer?" in O(1) without scanning the
 * raw matches list. Populated by:
 *   * ``contactProfessional`` thunk on successful submit (status='contacted')
 *   * ``contactProfessional`` thunk on 409 dedup (status='contacted', existingMatchId set)
 *   * ``hydrateMatchStatuses`` thunk on chat-session mount (cross-session
 *     persistence — if user contacted this lawyer last week, the chip
 *     still appears today).
 */
interface MatchStatusEntry {
  /** Null only when ``status === 'pending_invite'`` — the recipient was
      un-claimed at contact time, so no real ``professional_matches.id``
      exists yet. The post-claim promote materializes the row and the
      next hydrate-statuses sweep replaces this entry with the real
      ``matchId`` + ``status: 'contacted'``. */
  matchId: string | null;
  /** Mirrors backend MatchStatus literal values, plus the FE-only
      ``'pending_invite'`` variant for un-claimed-recipient inquiries
      (no backend match row exists yet). */
  status:
    | 'suggested'
    | 'contacted'
    | 'responded'
    | 'declined'
    | 'expired'
    | 'pending_invite';
  /** ISO timestamp — drives the "2h ago" relative-time chip. */
  sentAt: string | null;
  /** ISO timestamp of the most recent lawyer reply, when status='responded'. */
  lastReplyAt: string | null;
}

interface ProfessionalsState {
  // Search page state
  searchResults: ProfessionalRead[];
  searchTotal: number;
  searchPage: number;
  searchPageSize: number;
  searchFilters: SearchFilters;
  searchLoading: boolean;
  searchError: string | null;
  /**
   * HTTP status behind searchError when known (#8290). Only 429 is surfaced
   * today (nginx rate limit on POST /professionals/search) so the directory
   * can show a "too many searches" message instead of a silent empty state.
   */
  searchErrorStatus: number | null;
  /** Retry-After back-off hint (seconds) from a 429, when readable. */
  searchRetryAfterSeconds: number | null;
  /**
   * RTK requestId of the newest searchProfessionals dispatch (#8310 race
   * guard). fulfilled/rejected reducers ignore any action whose requestId
   * doesn't match — a slow page-1 response from a superseded search must not
   * clobber (or resurrect an error over) the newer request's state.
   */
  searchRequestId: string | null;
  /** Topic match scores + explanations (empty for pure-name searches) */
  topicScores: Record<string, TopicScoreEntry>;
  /** Map points from the search response */
  mapPoints: MapPoint[];
  /** True when top results have low match confidence (suggest broadening search) */
  lowConfidence: boolean;
  /** Query intent from classifier: "name" = pure name lookup, "ambiguous" = topic-enriched */
  queryIntent: 'name' | 'ambiguous';
  /**
   * Option-3 fallback tier (#9598): advocates with no declared specialization,
   * rendered in a SEPARATE labelled section below the primary results — never
   * merged into ``searchResults``. Only populated on a page-1 response whose
   * request applied a category filter AND the backend flag is on; preserved
   * across subsequent infinite-scroll pages (the backend only computes it for
   * page 1) rather than being cleared to empty on every page fetch.
   */
  fallbackAdvocates: ProfessionalRead[];

  // Current profile view
  currentProfile: ProfessionalRead | null;
  profileLoading: boolean;
  profileError: string | null;

  // Inline match results (keyed by sessionId)
  matchResults: Record<string, MatchResultsState>;

  // Phase 3.1: per-professional contact status (keyed by professional_id)
  matchStatusByProfessional: Record<string, MatchStatusEntry>;
  /** True while hydrateMatchStatuses is in flight on chat mount. */
  matchStatusHydrating: boolean;

  /**
   * #6036: the anonymized advocate-facing intake block the client prepared in the
   * case-prep flow. When set, ContactProfessionalDialog offers it as the consented
   * `shared_intake_summary` for whichever advocate the client contacts next —
   * unifying the directory + chat contact paths. Null = nothing prepared.
   */
  preparedIntakeSummary: string | null;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: ProfessionalsState = {
  // Search
  searchResults: [],
  searchTotal: 0,
  searchPage: 1,
  searchPageSize: 20,
  searchFilters: {
    profession_type: null,
    query_text: null,
    city: null,
    location_type: null,
    region_code: null,
    district: null,
    latitude: null,
    longitude: null,
    radius_km: null,
    registry_statuses: ['active'] as ProfessionalStatus[],
  },
  searchLoading: false,
  searchError: null,
  searchErrorStatus: null,
  searchRetryAfterSeconds: null,
  searchRequestId: null,
  topicScores: {},
  mapPoints: [],
  lowConfidence: false,
  queryIntent: 'ambiguous',
  fallbackAdvocates: [],

  // Profile
  currentProfile: null,
  profileLoading: false,
  profileError: null,

  // Match results
  matchResults: {},

  // Match-status surfacing
  matchStatusByProfessional: {},
  matchStatusHydrating: false,

  // #6036: prepared anonymized intake summary (nothing prepared yet)
  preparedIntakeSummary: null,
};

// ============================================================================
// CACHING
// ============================================================================

const profileCache = new CacheManager<ProfessionalRead>(
  'ProfessionalProfile',
  5 * 60 * 1000 // 5 minutes TTL
);

const searchCache = new CacheManager<{
  results: ProfessionalRead[];
  total: number;
  scores?: Record<string, TopicScoreEntry>;
  mapPoints?: MapPoint[];
  lowConfidence?: boolean;
  queryIntent?: 'name' | 'ambiguous';
  fallbackAdvocates?: ProfessionalRead[];
}>('ProfessionalsSearch', 2 * 60 * 1000); // 2 minutes TTL

/**
 * Generate cache key from search filters
 */
function getSearchCacheKey(
  filters: SearchFilters,
  page: number,
  pageSize: number
): string {
  return JSON.stringify({ ...filters, page, pageSize });
}

// ============================================================================
// ASYNC THUNKS
// ============================================================================

/**
 * Structured rejection payload for searchProfessionals (#8290) — carries the
 * HTTP status so the directory UI can special-case rate-limit (429)
 * rejections. Non-429 failures still reject with a plain string; the
 * rejected reducer accepts both shapes. (Deliberately not exported — no
 * external consumer yet; components read the derived state fields instead.)
 */
interface SearchRejection {
  message: string;
  status: number;
  retryAfterSeconds: number | null;
}

/**
 * Normalizes `null` to `undefined` so an optional request field is omitted
 * rather than sent as an explicit null.
 *
 * Exists so request builders can map a long list of optional filters without a
 * `?? undefined` on every line — each of those counts toward the enclosing
 * function's cognitive complexity, and there are two dozen of them here.
 */
function orUndefined<T>(value: T | null | undefined): T | undefined {
  return value ?? undefined;
}

/**
 * Maps the slice's {@link SearchFilters} onto the directory-search request
 * payload. Pure — extracted from the thunk so the wire-format mapping is
 * testable and reviewable on its own.
 */
function buildDirectorySearchRequest(
  filters: SearchFilters,
  page: number,
  pageSize: number
) {
  return {
    query: filters.query_text ?? '',
    profession_type: orUndefined(filters.profession_type),
    registry_statuses: orUndefined(filters.registry_statuses),
    location_type: orUndefined(filters.location_type),
    region_code: orUndefined(filters.region_code),
    district: orUndefined(filters.district),
    latitude: orUndefined(filters.latitude),
    longitude: orUndefined(filters.longitude),
    radius_km: orUndefined(filters.radius_km),
    city: orUndefined(filters.city),
    cities: orUndefined(filters.cities),
    region_codes: orUndefined(filters.region_codes),
    districts: orUndefined(filters.districts),
    // Legacy text-based specializations (deprecated — one-cycle).
    // Backend still accepts this for old client payloads.
    specializations: orUndefined(filters.specializations),
    // Stage-2b: taxonomy leaf UUIDs for the hard-gate filter.
    specialization_taxonomy_ids: orUndefined(
      filters.specialization_taxonomy_ids
    ),
    extracted_specializations: orUndefined(filters.extracted_specializations),
    languages: orUndefined(filters.languages),
    sort_by: orUndefined(filters.sort_by),
    page,
    page_size: pageSize,
    // Per-request v3 SERVE opt-in (#5494), set from ?v3_rank=true.
    v3_rank: orUndefined(filters.v3_rank),
  };
}

/**
 * Builds the structured 429 rejection payload (#8290) so the directory UI can
 * render a throttle message plus retry countdown.
 */
function toSearchRateLimitRejection(response: {
  message?: string;
  meta?: Record<string, unknown> | undefined;
}): SearchRejection {
  const retryAfter = response.meta?.['retryAfterSeconds'];
  return {
    message: response.message || 'Rate limit exceeded',
    status: 429,
    retryAfterSeconds: typeof retryAfter === 'number' ? retryAfter : null,
  };
}

/**
 * Phase 1: Fast name-only search via the unified backend endpoint (~50ms).
 * Map points are returned alongside list results — no separate fetch needed.
 */
export const searchProfessionals = createAsyncThunk(
  'professionals/search',
  async (
    params: {
      filters?: SearchFilters;
      page?: number;
      pageSize?: number;
    },
    { getState, rejectWithValue }
  ) => {
    const state = getState() as RootState;
    const filters = params.filters ?? state.professionals.searchFilters;
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? state.professionals.searchPageSize;

    try {
      const cacheKey = getSearchCacheKey(filters, page, pageSize);
      const cached = searchCache.get(cacheKey);

      if (cached) {
        return { ...cached, page, pageSize, filters };
      }

      const response = await ProfessionalsService.directorySearch(
        buildDirectorySearchRequest(filters, page, pageSize)
      );

      if (response.status === 'success' && response.data) {
        const data = {
          results: response.data.professionals,
          total: response.data.total,
          scores: response.data.topic_scores,
          mapPoints: response.data.map_points,
          lowConfidence: response.data.low_confidence,
          queryIntent: response.data.query_intent,
          // Option-3 fallback tier (#9598) — only ever populated on page 1;
          // see the state doc comment for why later pages don't clear it.
          fallbackAdvocates: response.data.fallback_advocates ?? [],
        };
        searchCache.set(cacheKey, data);
        return {
          ...data,
          page,
          pageSize,
          filters,
        };
      }

      // Rate-limited (nginx search_rl zone → 429, normalized into the ApiResp
      // envelope by the api-services interceptor). Reject with a structured
      // payload so the UI can show a throttle message + retry (#8290).
      if (response.code === 429) {
        return rejectWithValue(toSearchRateLimitRejection(response));
      }

      return rejectWithValue(response.message || 'Search failed');
    } catch (error) {
      return rejectWithValue(`Search failed: ${error}`);
    }
  }
);

/**
 * Fetch a single professional profile by ID.
 */
export const fetchProfessionalProfile = createAsyncThunk(
  'professionals/fetchProfile',
  async (professionalId: string, { rejectWithValue }) => {
    try {
      // Check cache
      const cached = profileCache.get(professionalId);
      if (cached) {
        return cached;
      }

      const response =
        await ProfessionalsService.getProfessional(professionalId);

      if (response.status === 'success' && response.data) {
        // Cache profile
        profileCache.set(professionalId, response.data);
        return response.data;
      }

      return rejectWithValue(response.message || 'Profile not found');
    } catch (error) {
      return rejectWithValue(`Failed to fetch profile: ${error}`);
    }
  }
);

/**
 * Maps the {@link findMatchingProfessionals} thunk args onto the find-matching
 * request payload, applying the backend's expected defaults (lawyer /
 * Slovak / 5 results / no geo signal).
 */
function buildFindMatchingRequest(params: {
  sessionId: string;
  professionType?: ProfessionType;
  question: string;
  clientCity?: string;
  clientCoordinates?: { lat: number; lng: number } | null;
  radiusKm?: number;
  clientLocationSource?: 'geolocation' | 'manual' | 'none';
  language?: string;
  limit?: number;
}) {
  return {
    // Default to lawyer if not specified
    profession_type: params.professionType ?? 'lawyer',
    question: params.question,
    client_city: params.clientCity,
    client_coordinates: params.clientCoordinates ?? null,
    radius_km: params.radiusKm,
    client_location_source: params.clientLocationSource ?? 'none',
    language: params.language ?? 'sk',
    limit: params.limit ?? 5,
    // Thread the chat session through so the audit-log row's session_id
    // correlates this match with the chat that triggered it (issue #3686).
    session_id: params.sessionId,
  };
}

/**
 * Find matching professionals for a research session.
 * Called from the "Find a lawyer" prompt in chat.
 *
 * Can be called with just sessionId (uses defaults) or with full context.
 */
export const findMatchingProfessionals = createAsyncThunk(
  'professionals/findMatching',
  async (
    params: {
      sessionId: string;
      /**
       * Per-AI-message key. Multiple "Find a lawyer" prompts can render
       * in the same chat session (one per AI answer), and each needs its
       * own loading / results bucket. When omitted, ``sessionId`` is used
       * as the bucket key — preserves behavior for non-chat callers.
       */
      messageId?: string;
      professionType?: ProfessionType;
      /**
       * User's natural-language question. Required — backend runs it
       * through QuestionAnalyzer (one LLM call, Pydantic structured
       * output) which returns both ``legal_areas`` (specialty scoring)
       * and a refined ``search_query`` (BM25 + embedding). No more
       * frontend-side ``areas`` placeholder.
       */
      question: string;
      clientCity?: string;
      /** Browser-geolocation coordinates. Null = no geo signal. */
      clientCoordinates?: { lat: number; lng: number } | null;
      /** Optional radius cap in km when coordinates are set. */
      radiusKm?: number;
      /** Origin of the coordinates for audit-log transparency. */
      clientLocationSource?: 'geolocation' | 'manual' | 'none';
      language?: string;
      limit?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await ProfessionalsService.findMatching(
        buildFindMatchingRequest(params)
      );

      if (response.status === 'success' && response.data) {
        return {
          // Map key for matchResults — per-message when caller supplies
          // messageId, otherwise per-session for back-compat.
          bucketKey: params.messageId ?? params.sessionId,
          sessionId: params.sessionId,
          matches: response.data.matches,
          featuredSlots: response.data.featured_slots ?? [],
          featuredSlotImpressionIds:
            response.data.featured_slot_impression_ids ?? [],
        };
      }

      return rejectWithValue(response.message || 'Matching failed');
    } catch (error) {
      return rejectWithValue(`Matching failed: ${error}`);
    }
  }
);

/**
 * Outcome shape returned by the contactProfessional thunk.
 *
 * Discriminated union so the calling component can render the appropriate
 * toast without re-parsing HTTP error codes:
 *
 *   - "created" — match persisted, success toast
 *   - "duplicate" — 409 from the 30-day dedup gate; existingMatchId lets
 *     the UI link directly to the existing thread
 *   - "rate_limited" — 429 from the per-user rate-limit; bucket + retryAfter
 *     drive a localized "try again in N min" toast
 *
 * Errors that don't fit these (network drop, 5xx, validation) reject the
 * thunk via rejectWithValue and surface as a generic error toast.
 */
type ContactProfessionalResult =
  | { status: 'created'; matchId: string }
  | { status: 'duplicate'; existingMatchId: string; windowDays: number }
  | {
      status: 'rate_limited';
      bucket: 'hourly' | 'daily';
      retryAfterSeconds: number;
    }
  | {
      // Returned when the recipient is an un-claimed SAK-registry lawyer.
      // The backend parks the inquiry as a ``pending_inquiry`` and the
      // cold-dispatcher cron mails the lawyer 90 min later. The UI should
      // show a "lawyer invited, awaiting their response" surface — there
      // is NO real match thread yet, so no ``matchId``.
      status: 'pending_invite';
      pendingInquiryId: string;
      professionalName: string;
      coldDispatchAt: string;
      expiresAt: string;
    };

export interface ContactProfessionalArgs {
  userId: string;
  professionalId: string;
  /**
   * Free-text question — required as of #4382 (SAK §21 redesign). Backend
   * Pydantic enforces min 20 chars + max 280; backend also redacts free-text
   * emails/phones/IBANs before forwarding to the lawyer.
   */
  questionSummary: string;
  /**
   * Inquirer's self-declared full name — required as of #4382. Prefilled
   * on the dialog from the Supabase auth account's ``user_metadata.full_name``
   * when available; user can edit before submit. Drives the lawyer's
   * §21 conflict check before any substantive reply.
   */
  clientName: string;
  /**
   * Adverse party (if any) — optional input to the §21 conflict check.
   * Free text up to 300 chars; backend stores verbatim, no normalization.
   */
  opposingParty?: string;
  /** Coarse city/region — never street-level. */
  clientLocation?: string;
  /** Origin chat session, when contact happens from a research session card. */
  sessionId?: string;
  matchScore?: number;
  /**
   * Issue #5835 — consented document-intake disclosure at handoff. The
   * distilled #5831 intake summary the user OPTED IN to forward to this
   * specific lawyer. Sent only when ``intakeShareConsent`` is true; the
   * backend re-validates the gate (consent + early-access) and drops it
   * otherwise. Defaults to "share nothing".
   */
  sharedIntakeSummary?: string;
  /** Explicit per-lawyer opt-in for {@link sharedIntakeSummary}. Unchecked by default. */
  intakeShareConsent?: boolean;
  /**
   * Issue #5957 — document-level consent. The user re-uploaded the original
   * document AT share time (raw bytes ephemeral in #5831; re-upload-on-share
   * preserves data-minimisation). When true the backend logs a
   * share_kind='document' consent event. Lawyer-facing rendering: #5959.
   * Unchecked by default — sharing is never automatic.
   */
  intakeShareDocumentConsent?: boolean;
}

/**
 * Backend error-detail shapes for the two structured non-2xx responses
 * `POST /professionals/matches` returns. Mirrored from the FastAPI handler
 * in ``backend/.../api/professionals/matching.py::create_match`` — keep
 * these field names in sync with the backend HTTPException ``detail``.
 */
interface MatchDuplicateDetail {
  message?: string;
  existing_match_id: string;
  window_days: number;
}

interface MatchRateLimitDetail {
  message?: string;
  bucket: 'hourly' | 'daily';
  retry_after_seconds: number;
  hourly_max?: number;
  daily_max?: number;
}

type MatchErrorDetail = MatchDuplicateDetail | MatchRateLimitDetail;

/**
 * Shape of the ApiResp envelope when ``apiClient`` resolves a non-2xx
 * response (the 429 path in ``services/api-services.ts``). The shared
 * ``ApiResp`` type doesn't model ``detail`` because most existing callers
 * don't read it; this local extension gives the thunk a typed surface
 * for the 429 branch without leaking ``any`` into the slice.
 */
interface MatchErrorEnvelope {
  status: 'error' | 'errors';
  code?: number;
  message?: string;
  detail?: MatchErrorDetail;
}

/**
 * Body shape axios receives in `error.response.data` for 409 / 5xx — the
 * paths the shared interceptor in `services/api-services.ts` routes via
 * `Promise.reject`. ``detail`` mirrors the FastAPI HTTPException body.
 */
interface MatchAxiosErrorBody {
  detail?: MatchErrorDetail | string;
}

type MatchAxiosError = AxiosError<MatchAxiosErrorBody>;

function isMatchErrorEnvelope(raw: object): raw is MatchErrorEnvelope {
  return 'code' in raw && 'status' in raw;
}

function isDuplicateDetail(
  detail: MatchErrorDetail | string | undefined
): detail is MatchDuplicateDetail {
  if (typeof detail !== 'object' || detail === null) return false;
  return (
    'existing_match_id' in detail &&
    typeof detail.existing_match_id === 'string'
  );
}

function isRateLimitDetail(
  detail: MatchErrorDetail | string | undefined
): detail is MatchRateLimitDetail {
  if (typeof detail !== 'object' || detail === null) return false;
  return (
    'bucket' in detail &&
    (detail.bucket === 'hourly' || detail.bucket === 'daily')
  );
}

/**
 * Extract a typed structured error from a raw response or AxiosError.
 *
 * The shared `apiClient` in `services/api-services.ts` has split behavior by
 * status code:
 *
 *   * 402 / 409 / 500 → axios `Promise.reject(error)`. The thunk's `catch`
 *     block sees an `AxiosError<MatchAxiosErrorBody>` whose
 *     ``response.data.detail`` matches the FastAPI HTTPException body.
 *   * 429 (and other non-special non-2xx) → `Promise.resolve(error.response.data)`.
 *     The thunk's normal flow sees a ``MatchErrorEnvelope`` with ``code``
 *     and ``detail`` populated.
 *
 * Returns the unified ``(code, detail)`` tuple so the thunk has one branch
 * per status code, not two. Inputs are typed (``MatchAxiosError`` or
 * ``MatchErrorEnvelope``) — the catch-block ``unknown`` is narrowed at the
 * call site via ``isAxiosError`` / ``isMatchErrorEnvelope`` before being
 * passed in.
 */
function unpackContactErrorFromAxios(err: MatchAxiosError): {
  code: number | undefined;
  detail: MatchErrorDetail | undefined;
} {
  const detail = err.response?.data?.detail;
  return {
    code: err.response?.status,
    detail: typeof detail === 'object' ? detail : undefined,
  };
}

function unpackContactErrorFromEnvelope(env: MatchErrorEnvelope): {
  code: number | undefined;
  detail: MatchErrorDetail | undefined;
} {
  return { code: env.code, detail: env.detail };
}

/**
 * Cross-session contact-status hydration (#3557 Phase 3.4).
 *
 * Fired on chat-session mount: fetches the user's match list and
 * populates ``matchStatusByProfessional`` so the carousel cards know
 * "user already contacted this lawyer last week" without forcing them
 * to remember. Backed by ``GET /professionals/matches/me`` (already
 * shipped pre-#3557).
 *
 * Best-effort: thunk rejection is silent — the chat session loads
 * either way; cards just lose their status pills until next mount.
 */
export const hydrateMatchStatuses = createAsyncThunk<
  Record<string, MatchStatusEntry>,
  void,
  { rejectValue: string }
>('professionals/hydrateMatchStatuses', async (_args, { rejectWithValue }) => {
  try {
    const response = await ProfessionalsService.getMyMatches();
    if (response.status === 'success' && Array.isArray(response.data)) {
      const map: Record<string, MatchStatusEntry> = {};
      for (const m of response.data) {
        // Backend returns an array of ProfessionalMatch — fields match
        // the Pydantic model 1:1. Use camelCase locally to match the
        // rest of the slice's conventions.
        const sentAt =
          (m.contacted_at as string | null) ??
          (m.created_at as string | null) ??
          null;
        // ``messages`` is JSONB[] — find the most recent professional-side
        // message for the lastReplyAt signal that drives the "lawyer
        // replied" pill (Phase 3.3).
        const lastReply = (m.messages ?? [])
          .filter((msg) => msg.sender_type === 'professional')
          .map((msg) => msg.at as string | null)
          .filter((x): x is string => Boolean(x))
          .sort()
          .pop();
        map[m.professional_id as string] = {
          matchId: m.id as string,
          status: m.status as MatchStatusEntry['status'],
          sentAt,
          lastReplyAt: lastReply ?? null,
        };
      }
      return map;
    }
    return rejectWithValue('Failed to load match statuses');
  } catch (error) {
    return rejectWithValue(`Failed to load match statuses: ${error}`);
  }
});

/**
 * Send a "Kontaktovať" inquiry from the chat carousel or directory.
 *
 * Wraps `ProfessionalsService.createMatch(...)`. Translates the backend's
 * 409 (recent duplicate) and 429 (rate-limit) into typed result variants
 * the UI can dispatch toasts on without re-parsing HTTP — see
 * :class:`ContactProfessionalResult`.
 *
 * Other failures (network drop, 5xx, validation 400) reject via
 * rejectWithValue with a translation-key string the toast layer maps to
 * a localized error message.
 */
/**
 * Maps a `(code, detail)` pair onto a typed result variant. Returns `null`
 * when the pair is not a structured error we know how to handle — the caller
 * falls through to the generic-error path.
 *
 * Hoisted to module scope (rather than nested in the thunk) so both the
 * envelope and the axios error path share one implementation.
 */
function toStructuredContactResult(
  code: number | undefined,
  detail: MatchErrorDetail | undefined
): ContactProfessionalResult | null {
  if (code === 409 && isDuplicateDetail(detail)) {
    return {
      status: 'duplicate',
      existingMatchId: detail.existing_match_id,
      windowDays: detail.window_days,
    };
  }
  if (code === 429 && isRateLimitDetail(detail)) {
    return {
      status: 'rate_limited',
      bucket: detail.bucket,
      retryAfterSeconds: detail.retry_after_seconds,
    };
  }
  return null;
}

/**
 * The inquiry fields shared verbatim by the single-recipient
 * ({@link contactProfessional}) and bulk ({@link contactProfessionalsBulk})
 * contact paths — so a consent-handling change cannot land on one and miss
 * the other.
 *
 * ``MatchCreate`` types nullable fields as ``string | null`` — TypeScript
 * strict's ``exactOptionalPropertyTypes`` rejects bare ``undefined``, so
 * missing optional values coerce to ``null`` to match the backend schema.
 * ``question_summary`` and ``client_name`` are required (issue #4382) and pass
 * through verbatim.
 */
function buildSharedInquiryFields(args: {
  questionSummary: string;
  clientName: string;
  opposingParty?: string;
  clientLocation?: string;
  sessionId?: string;
  sharedIntakeSummary?: string;
  intakeShareConsent?: boolean;
  intakeShareDocumentConsent?: boolean;
}) {
  return {
    question_summary: args.questionSummary,
    client_name: args.clientName,
    opposing_party: args.opposingParty ?? null,
    client_location: args.clientLocation ?? null,
    session_id: args.sessionId ?? null,
    // #5835: only forward the summary when the user explicitly opted in; the
    // backend re-validates consent + early-access and drops it otherwise.
    // ``intake_share_consent`` defaults to false (share nothing).
    shared_intake_summary: args.intakeShareConsent
      ? (args.sharedIntakeSummary ?? null)
      : null,
    intake_share_consent: args.intakeShareConsent ?? false,
    // #5957: document-level consent flag — the user re-uploaded the original
    // document at share time; backend logs share_kind='document'.
    intake_share_document_consent: args.intakeShareDocumentConsent ?? false,
  };
}

/**
 * The two success shapes `POST /matches` can return: the regular match
 * envelope, or the un-claimed-recipient parking receipt.
 */
type ContactSuccessPayload =
  | MatchCreateResponse
  | {
      status: 'pending_invite';
      pending_inquiry_id: string;
      professional_id: string;
      professional_name: string;
      cold_dispatch_at: string;
      expires_at: string;
    };

/**
 * Maps a successful create-match response onto a typed result.
 *
 * Discriminator: the un-claimed-recipient path returns
 * ``{ status: 'pending_invite', pending_inquiry_id, ... }`` instead of the
 * regular match envelope. Detected via the presence of ``pending_inquiry_id``
 * so we don't fork the OpenAPI schema for a single optional surface.
 */
function toContactSuccessResult(
  data: ContactSuccessPayload
): ContactProfessionalResult {
  if ('pending_inquiry_id' in data) {
    return {
      status: 'pending_invite',
      pendingInquiryId: data.pending_inquiry_id,
      professionalName: data.professional_name,
      coldDispatchAt: data.cold_dispatch_at,
      expiresAt: data.expires_at,
    };
  }
  return { status: 'created', matchId: data.id };
}

/**
 * Resolves a structured contact result from a *resolved* error envelope — the
 * 429 path, where `code` is populated on an otherwise-successful HTTP
 * response. Narrows from the wider ApiResp shape via the structural type
 * guard, then hands off to the typed unpacker: no ``any`` / ``unknown``
 * escapes. Returns `null` when this is not a structured error.
 */
function structuredResultFromEnvelope(
  response: unknown
): ContactProfessionalResult | null {
  if (
    typeof response !== 'object' ||
    response === null ||
    !isMatchErrorEnvelope(response)
  ) {
    return null;
  }
  const { code, detail } = unpackContactErrorFromEnvelope(response);
  return toStructuredContactResult(code, detail);
}

/**
 * Resolves a structured contact result from a *rejected* axios error — the 409
 * path. 5xx and network errors return `null` and fall through to the generic
 * toast. ``isAxiosError`` is axios's own type guard and yields a fully-typed
 * AxiosError.
 */
function structuredResultFromAxiosError(
  error: unknown
): ContactProfessionalResult | null {
  if (!isAxiosError<MatchAxiosErrorBody>(error)) {
    return null;
  }
  const { code, detail } = unpackContactErrorFromAxios(error);
  return toStructuredContactResult(code, detail);
}

export const contactProfessional = createAsyncThunk<
  ContactProfessionalResult,
  ContactProfessionalArgs,
  { rejectValue: string }
>('professionals/contact', async (args, { rejectWithValue }) => {
  try {
    const response = await ProfessionalsService.createMatch({
      user_id: args.userId,
      professional_id: args.professionalId,
      match_score: args.matchScore ?? 0.5,
      ...buildSharedInquiryFields(args),
    });

    if (response.status === 'success' && response.data) {
      return toContactSuccessResult(response.data as ContactSuccessPayload);
    }

    const fromEnvelope = structuredResultFromEnvelope(response);
    if (fromEnvelope) return fromEnvelope;

    return rejectWithValue(response.message || 'Lawyers.contact.genericError');
  } catch (error) {
    const fromAxios = structuredResultFromAxiosError(error);
    if (fromAxios) return fromAxios;

    return rejectWithValue('Lawyers.contact.genericError');
  }
});

/**
 * Shared inquiry payload for {@link contactProfessionalsBulk} — the fields
 * identical for every selected advocate. Mirrors {@link ContactProfessionalArgs}
 * minus the single-recipient ``professionalId`` / ``matchScore`` (those live
 * per-recipient on {@link BulkContactRecipientArg}).
 */
export interface ContactProfessionalsBulkArgs {
  userId: string;
  /** The advocates to contact — id + that card's own match score. */
  recipients: BulkContactRecipientArg[];
  questionSummary: string;
  clientName: string;
  opposingParty?: string;
  clientLocation?: string;
  sessionId?: string;
  /** #5835 — consented document-intake disclosure, applied per recipient. */
  sharedIntakeSummary?: string;
  intakeShareConsent?: boolean;
  /** #5957 — document-level consent, applied per recipient. */
  intakeShareDocumentConsent?: boolean;
}

/** One selected advocate inside a {@link ContactProfessionalsBulkArgs}. */
export interface BulkContactRecipientArg {
  professionalId: string;
  matchScore?: number;
}

/**
 * Outcome of {@link contactProfessionalsBulk}. ``results`` is index-decoupled
 * from the request (keyed by ``professional_id``) so the UI can reconcile each
 * advocate's per-recipient outcome regardless of order. The thunk only rejects
 * on a transport/5xx failure that yielded no result list at all; partial
 * success (some sent, some already-contacted) is a fulfilled outcome.
 */
interface ContactProfessionalsBulkResult {
  results: BulkMatchRecipientResult[];
}

/**
 * Send the SAME inquiry to several advocates in one click — bulk contact
 * (#6046). Wraps {@link ProfessionalsService.createMatchesBulk}.
 *
 * The backend always returns 200 with per-recipient outcomes, so the happy
 * path is a single fulfilled action carrying the full result list. The
 * fulfilled reducer writes a ``matchStatusByProfessional`` entry for every
 * ``sent`` / ``pending_invite`` / ``already_contacted`` advocate so their cards
 * flip to the "already contacted" state immediately — exactly as the
 * single-contact path does. ``rate_limited`` / ``unavailable`` / ``failed``
 * leave the slice untouched (the caller's summary toast handles UX).
 *
 * Rejects only when the request never produced a result list (network drop,
 * 5xx) — the caller shows a generic error toast and keeps the selection.
 */
export const contactProfessionalsBulk = createAsyncThunk<
  ContactProfessionalsBulkResult,
  ContactProfessionalsBulkArgs,
  { rejectValue: string }
>('professionals/contactBulk', async (args, { rejectWithValue }) => {
  try {
    const recipients: BulkMatchRecipient[] = args.recipients.map((r) => ({
      professional_id: r.professionalId,
      match_score: r.matchScore ?? 0.5,
    }));

    // Consent fields are shared with the single-contact path; the backend
    // re-validates them per recipient.
    const response = await ProfessionalsService.createMatchesBulk({
      user_id: args.userId,
      recipients,
      ...buildSharedInquiryFields(args),
    });

    if (
      response.status === 'success' &&
      response.data &&
      Array.isArray(response.data.results)
    ) {
      return { results: response.data.results };
    }

    return rejectWithValue(response.message || 'Lawyers.contact.genericError');
  } catch (error) {
    return rejectWithValue(`Lawyers.contact.genericError: ${error}`);
  }
});

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Applies a search response's topic scores to the slice.
 *
 * Scores arrive merged into the single search response (the server RRF-fuses
 * name + topic), so they may already be populated on phase 1. Page 1 replaces
 * them; later pages merge, so infinite scroll accumulates scores rather than
 * dropping earlier pages'. A page-1 response carrying no scores clears them.
 */
function applyTopicScores(
  state: ProfessionalsState,
  payload: { page: number; scores?: Record<string, TopicScoreEntry> }
): void {
  const scores = payload.scores;
  if (scores && Object.keys(scores).length > 0) {
    state.topicScores =
      payload.page === 1 ? scores : { ...state.topicScores, ...scores };
    return;
  }
  if (payload.page === 1) {
    state.topicScores = {};
  }
}

/**
 * Writes a fulfilled search response into the slice.
 *
 * Called only after the caller has confirmed the response is not stale
 * (#8310); the freshness guard stays in the reducer so the ignore-path is
 * visible at the dispatch site.
 */
function applySearchSuccess(
  state: ProfessionalsState,
  payload: {
    page: number;
    total: number;
    results: ProfessionalRead[];
    scores?: Record<string, TopicScoreEntry>;
    mapPoints?: MapPoint[];
    lowConfidence?: boolean;
    queryIntent?: 'name' | 'ambiguous';
    fallbackAdvocates?: ProfessionalRead[];
  }
): void {
  state.searchLoading = false;
  // Success replaces any previous failure — clear the error trio here (not in
  // pending) so the banner survives the retry round-trip.
  state.searchError = null;
  state.searchErrorStatus = null;
  state.searchRetryAfterSeconds = null;
  state.searchResults =
    payload.page === 1
      ? payload.results
      : [...state.searchResults, ...payload.results];
  state.searchTotal = payload.total;
  state.searchPage = payload.page;

  applyTopicScores(state, payload);

  // Store map points
  if (payload.mapPoints) {
    state.mapPoints = payload.mapPoints;
  }
  state.lowConfidence = payload.lowConfidence ?? false;
  state.queryIntent = payload.queryIntent ?? 'ambiguous';

  // Option-3 fallback tier (#9598): the backend only computes this for page 1
  // (it's a "below the fold" static section, not paginated), so a page-2+
  // fetch must NOT overwrite it with the empty array a later page returns.
  if (payload.page === 1) {
    state.fallbackAdvocates = payload.fallbackAdvocates ?? [];
  }
}

/**
 * Maps one bulk-contact recipient outcome onto the match-status entry to
 * write, or `null` for the outcomes that leave the slice untouched
 * (``rate_limited`` / ``unavailable`` / ``failed`` — the caller's summary
 * toast handles their UX).
 */
function bulkOutcomeToMatchStatus(
  result: BulkMatchRecipientResult,
  now: string,
  previousSentAt: string | null
): MatchStatusEntry | null {
  switch (result.outcome) {
    case 'sent':
      return {
        matchId: result.match_id ?? null,
        status: 'contacted',
        sentAt: now,
        lastReplyAt: null,
      };
    case 'already_contacted':
      return {
        matchId: result.existing_match_id ?? null,
        // Upstream status unknown without a refetch; 'contacted' is the best
        // default — the next hydrateMatchStatuses sweep corrects it.
        status: 'contacted',
        sentAt: previousSentAt ?? now,
        lastReplyAt: null,
      };
    case 'pending_invite':
      return {
        matchId: null,
        status: 'pending_invite',
        sentAt: now,
        lastReplyAt: null,
      };
    default:
      return null;
  }
}

// ============================================================================
// SLICE
// ============================================================================

const professionalsSlice = createSlice({
  name: 'professionals',
  initialState,
  reducers: {
    // Search filters
    setSearchFilters: (state, action: PayloadAction<SearchFilters>) => {
      state.searchFilters = {
        ...state.searchFilters,
        ...action.payload,
      };
    },

    updateSearchFilter: (
      state,
      action: PayloadAction<Partial<SearchFilters>>
    ) => {
      state.searchFilters = { ...state.searchFilters, ...action.payload };
    },

    clearSearchFilters: (state) => {
      state.searchFilters = { ...initialState.searchFilters };
      state.searchResults = [];
      state.searchTotal = 0;
      state.searchPage = 1;
      state.fallbackAdvocates = [];
    },

    // #6036: set/clear the prepared anonymized advocate-facing intake summary.
    // Set when the case-prep flow finishes anonymization; cleared (null) to drop it.
    setPreparedIntakeSummary: (state, action: PayloadAction<string | null>) => {
      state.preparedIntakeSummary = action.payload;
    },

    /**
     * Seed Redux search state from a server-rendered initial result set (#6543).
     *
     * Called once on mount by ProfessionalDirectoryContent when the page server-
     * component has already fetched the first page. Only applies when the slice
     * is in its pristine initial state (searchResults empty, page 1, not loading)
     * to avoid clobbering a result set the user has already refined via filters.
     *
     * The subsequent searchProfessionals thunk (triggered by the effectiveFilters
     * useEffect) will re-confirm the same data via the client path — the seeded
     * results just guarantee something is visible in the SSR HTML on first paint.
     */
    seedInitialSearchResults: (
      state,
      action: PayloadAction<{
        results: ProfessionalRead[];
        total: number;
      }>
    ) => {
      // Guard: only seed when the slice is still in the pristine empty state.
      // If the user arrived via in-app navigation (filters already applied,
      // results already loaded), we must not overwrite their live result set.
      if (
        state.searchResults.length === 0 &&
        state.searchTotal === 0 &&
        state.searchPage === 1 &&
        !state.searchLoading
      ) {
        state.searchResults = action.payload.results;
        state.searchTotal = action.payload.total;
      }
    },

    // Clear current profile
    clearCurrentProfile: (state) => {
      state.currentProfile = null;
      state.profileError = null;
    },

    // Match results management (for chat integration)
    toggleMatchResultsExpanded: (
      state,
      action: PayloadAction<{ sessionId: string }>
    ) => {
      const { sessionId } = action.payload;
      if (state.matchResults[sessionId]) {
        state.matchResults[sessionId].isExpanded =
          !state.matchResults[sessionId].isExpanded;
      }
    },

    dismissMatchResults: (
      state,
      action: PayloadAction<{ sessionId: string; messageId?: string }>
    ) => {
      // Mirror the bucket-key resolution used by the find thunk: prefer
      // messageId when present so each AI answer's prompt dismisses
      // independently; fall back to sessionId otherwise.
      const key = action.payload.messageId ?? action.payload.sessionId;
      if (state.matchResults[key]) {
        state.matchResults[key].dismissed = true;
        state.matchResults[key].isExpanded = false;
      }
    },

    clearMatchResults: (
      state,
      action: PayloadAction<{ sessionId: string }>
    ) => {
      const { sessionId } = action.payload;
      delete state.matchResults[sessionId];
    },

    // Clear all match results (e.g., on logout)
    clearAllMatchResults: (state) => {
      state.matchResults = {};
    },
  },
  extraReducers: (builder) => {
    // Search professionals (phase 1: name)
    builder
      .addCase(searchProfessionals.pending, (state, action) => {
        state.searchLoading = true;
        // Record the newest request so stale fulfilled/rejected responses can
        // be ignored (#8310 race guard). Raw-action test dispatches may omit
        // meta, hence the optional chain.
        state.searchRequestId = action.meta?.requestId ?? null;
        // Deliberately NOT clearing the error trio here (#8310 a11y): the
        // banner must stay mounted while a retry is in flight so the
        // role=alert region isn't torn down and re-announced on every
        // attempt. fulfilled clears it; a new rejected overwrites it.
        state.topicScores = {};
        state.lowConfidence = false;
      })
      .addCase(searchProfessionals.fulfilled, (state, action) => {
        // Stale response from a superseded search — ignore (#8310).
        if (action.meta?.requestId !== state.searchRequestId) return;
        applySearchSuccess(state, action.payload);
      })
      .addCase(searchProfessionals.rejected, (state, action) => {
        // Stale rejection from a superseded search — ignore (#8310): a slow
        // 429 must not resurrect an error banner over a newer request.
        if (action.meta?.requestId !== state.searchRequestId) return;
        state.searchLoading = false;
        const payload = action.payload;
        if (payload && typeof payload === 'object') {
          // Structured rejection (#8290) — currently only the 429 path.
          const rejection = payload as SearchRejection;
          state.searchError = rejection.message;
          state.searchErrorStatus = rejection.status;
          state.searchRetryAfterSeconds = rejection.retryAfterSeconds;
        } else {
          state.searchError =
            (payload as string | undefined) ?? 'Search failed';
          state.searchErrorStatus = null;
          state.searchRetryAfterSeconds = null;
        }
      });

    // Fetch profile
    builder
      .addCase(fetchProfessionalProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(fetchProfessionalProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.currentProfile = action.payload;
      })
      .addCase(fetchProfessionalProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload as string;
      });

    // Find matching professionals — keyed by messageId when supplied
    // (one bucket per AI answer in chat), otherwise by sessionId.
    builder
      .addCase(findMatchingProfessionals.pending, (state, action) => {
        const key = action.meta.arg.messageId ?? action.meta.arg.sessionId;
        state.matchResults[key] = {
          professionals: [],
          featuredSlots: [],
          featuredSlotImpressionIds: [],
          loading: true,
          error: null,
          isExpanded: true, // Auto-expand when loading
          dismissed: false,
        };
      })
      .addCase(findMatchingProfessionals.fulfilled, (state, action) => {
        const { bucketKey, matches, featuredSlots, featuredSlotImpressionIds } =
          action.payload;
        if (state.matchResults[bucketKey]) {
          state.matchResults[bucketKey].loading = false;
          state.matchResults[bucketKey].professionals = matches;
          state.matchResults[bucketKey].featuredSlots = featuredSlots;
          state.matchResults[bucketKey].featuredSlotImpressionIds =
            featuredSlotImpressionIds;
        }
      })
      .addCase(findMatchingProfessionals.rejected, (state, action) => {
        const key = action.meta.arg.messageId ?? action.meta.arg.sessionId;
        if (state.matchResults[key]) {
          state.matchResults[key].loading = false;
          state.matchResults[key].error = action.payload as string;
        }
      })

      // Cross-session match-status hydration (#3557 Phase 3.4) — fired on
      // chat-session mount so the carousel's "already contacted" pills
      // survive page reloads.
      .addCase(hydrateMatchStatuses.pending, (state) => {
        state.matchStatusHydrating = true;
      })
      .addCase(hydrateMatchStatuses.fulfilled, (state, action) => {
        state.matchStatusHydrating = false;
        state.matchStatusByProfessional = action.payload;
      })
      .addCase(hydrateMatchStatuses.rejected, (state) => {
        state.matchStatusHydrating = false;
        // Silent: chat session loads regardless. Cards lose their
        // status pill until next mount. No toast — the lawyer-side
        // notification still fired so the user-facing flow isn't broken.
      })

      // Contact-professional outcomes (#3557 Phase 3.1) — write the
      // status entry into ``matchStatusByProfessional`` so the carousel
      // card's status chip / reply pill picks it up immediately.
      .addCase(contactProfessional.fulfilled, (state, action) => {
        const { professionalId } = action.meta.arg;
        const result = action.payload;
        const now = new Date().toISOString();
        if (result.status === 'created') {
          state.matchStatusByProfessional[professionalId] = {
            matchId: result.matchId,
            status: 'contacted',
            sentAt: now,
            lastReplyAt: null,
          };
        } else if (result.status === 'duplicate') {
          // Backend already had a match within the dedup window; surface
          // the existing one so the card pill links to that thread.
          state.matchStatusByProfessional[professionalId] = {
            matchId: result.existingMatchId,
            // We don't know the upstream status without a refetch;
            // 'contacted' is the best default — Phase 3.4's hydrate
            // call on next chat mount overwrites with the real status.
            status: 'contacted',
            sentAt:
              state.matchStatusByProfessional[professionalId]?.sentAt ?? now,
            lastReplyAt: null,
          };
        } else if (result.status === 'pending_invite') {
          // Un-claimed recipient: the backend parked the inquiry; no real
          // match thread exists yet. Surface a distinct status so the
          // carousel card renders an "Invited" pill instead of a regular
          // "Contacted" chip — i18n key
          // ``Lawyers.contact.pendingInvite`` carries the lawyer-name +
          // dispatch-window copy.
          state.matchStatusByProfessional[professionalId] = {
            matchId: null,
            status: 'pending_invite',
            sentAt: now,
            lastReplyAt: null,
          };
        }
        // Rate-limited / error outcomes don't touch the slice — caller's
        // toast handles UX, no persistent state change.
      })

      // Bulk-contact outcomes (#6046) — write a status entry for every
      // advocate that reached a terminal "contacted-ish" state so each card
      // flips to its already-contacted surface immediately, exactly like the
      // single-contact path. ``rate_limited`` / ``unavailable`` / ``failed``
      // leave the slice untouched.
      .addCase(contactProfessionalsBulk.fulfilled, (state, action) => {
        const now = new Date().toISOString();
        for (const result of action.payload.results) {
          const professionalId = result.professional_id;
          const entry = bulkOutcomeToMatchStatus(
            result,
            now,
            state.matchStatusByProfessional[professionalId]?.sentAt ?? null
          );
          if (entry) {
            state.matchStatusByProfessional[professionalId] = entry;
          }
        }
      });
  },
});

// ============================================================================
// EXPORTS
// ============================================================================

export const {
  setSearchFilters,
  updateSearchFilter,
  clearSearchFilters,
  setPreparedIntakeSummary,
  clearCurrentProfile,
  toggleMatchResultsExpanded,
  dismissMatchResults,
  clearMatchResults,
  clearAllMatchResults,
  seedInitialSearchResults,
} = professionalsSlice.actions;

// Selectors
export const selectSearchResults = (state: RootState) =>
  state.professionals.searchResults;
export const selectSearchTotal = (state: RootState) =>
  state.professionals.searchTotal;
export const selectSearchLoading = (state: RootState) =>
  state.professionals.searchLoading;
export const selectSearchError = (state: RootState) =>
  state.professionals.searchError;
export const selectSearchErrorStatus = (state: RootState) =>
  state.professionals.searchErrorStatus;
export const selectSearchRetryAfterSeconds = (state: RootState) =>
  state.professionals.searchRetryAfterSeconds;
export const selectSearchFilters = (state: RootState) =>
  state.professionals.searchFilters;
export const selectSearchPage = (state: RootState) =>
  state.professionals.searchPage;
export const selectHasMoreResults = (state: RootState) =>
  state.professionals.searchResults.length < state.professionals.searchTotal;
export const selectTopicScores = (state: RootState) =>
  state.professionals.topicScores;
export const selectMapPoints = (state: RootState) =>
  state.professionals.mapPoints;
export const selectLowConfidence = (state: RootState) =>
  state.professionals.lowConfidence;
export const selectQueryIntent = (state: RootState) =>
  state.professionals.queryIntent;
/** Option-3 fallback tier (#9598) — advocates with no declared specialization. */
export const selectFallbackAdvocates = (state: RootState) =>
  state.professionals.fallbackAdvocates;

/** #6036: the anonymized advocate-facing intake block the client prepared, if any. */
export const selectPreparedIntakeSummary = (state: RootState) =>
  state.professionals.preparedIntakeSummary;

export const selectCurrentProfile = (state: RootState) =>
  state.professionals.currentProfile;
export const selectProfileLoading = (state: RootState) =>
  state.professionals.profileLoading;

export const selectMatchResults = (sessionId: string) => (state: RootState) =>
  state.professionals.matchResults[sessionId];

// Selector for use with useAppSelector (returns function for sessionId)
export const selectMatchResultsBySession = (
  state: RootState,
  sessionId: string
): {
  matches: MatchedProfessional[];
  featuredSlots: MatchedProfessional[];
  featuredSlotImpressionIds: string[];
  dismissed: boolean;
} | null => {
  const result = state.professionals.matchResults[sessionId];
  if (!result) return null;
  return {
    matches: result.professionals,
    featuredSlots: result.featuredSlots,
    featuredSlotImpressionIds: result.featuredSlotImpressionIds,
    dismissed: result.dismissed,
  };
};

// Global loading state for matching
export const selectMatchLoading = (state: RootState): boolean => {
  return Object.values(state.professionals.matchResults).some((r) => r.loading);
};

/**
 * Per-bucket loading flag. Pass the same key the prompt uses
 * (``messageId ?? sessionId``) so each AI answer's prompt has its own
 * spinner — the global ``selectMatchLoading`` returns true whenever ANY
 * bucket is loading, which is why every mounted prompt lit up at once.
 */
export const selectMatchLoadingBySession = (
  state: RootState,
  key: string
): boolean => state.professionals.matchResults[key]?.loading ?? false;

export default professionalsSlice.reducer;
